import { lazy, Suspense, useEffect, useMemo } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Theme } from '@astryxdesign/core/theme';
import { publicApi } from '@/lib/api.ts';
import { baseLightTheme, baseTheme, tenantTheme } from '@/lib/theme.ts';
import { industryCopy, shellMode } from '@/lib/industry.ts';
import { registerTenantWorker } from '@/lib/pwa.ts';
import { ErrorView, LoadingView } from '@/components/StateViews.tsx';
import { ClientShell } from '@/features/public/ClientShell.tsx';
import { HomePage } from '@/features/public/HomePage.tsx';
import { ServicesPage } from '@/features/public/ServicesPage.tsx';
import { MyBookingsPage } from '@/features/my/MyBookingsPage.tsx';
import { NotFoundView } from '@/components/NotFoundView.tsx';
import { TenantContext } from './tenant-context.ts';

// The owner cabinet and the assistant load on demand: the booking path stays light.
const OwnerRoot = lazy(() => import('@/features/owner/OwnerRoot.tsx').then((m) => ({ default: m.OwnerRoot })));
const AssistantPage = lazy(() => import('@/features/assistant/AssistantPage.tsx').then((m) => ({ default: m.AssistantPage })));

const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,46}[a-z0-9])?$/;

export function TenantRoot() {
  const { slug = '' } = useParams();
  const valid = SLUG_RE.test(slug);
  const q = useQuery({
    queryKey: ['tenant', slug],
    queryFn: ({ signal }) => publicApi.tenant(slug, signal),
    enabled: valid,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (valid) void registerTenantWorker(slug);
  }, [slug, valid]);

  const ctx = useMemo(() => (q.data ? { slug, tenant: q.data, base: `/s/${slug}/` } : null), [q.data, slug]);

  // Before the studio loads, the shell's color-scheme meta picks light or dark.
  const early = shellMode();

  if (!valid) {
    return (
      <Theme theme={early === 'light' ? baseLightTheme : baseTheme} mode={early}>
        <NotFoundView title="Страница не найдена" />
      </Theme>
    );
  }
  if (!ctx) {
    return (
      <Theme theme={early === 'light' ? baseLightTheme : baseTheme} mode={early}>
        {q.isError ? <ErrorView error={q.error} onRetry={() => q.refetch()} fullScreen /> : <LoadingView fullScreen label="Загружаем…" />}
      </Theme>
    );
  }

  const { mode } = industryCopy(ctx.tenant.content);
  return (
    <Theme theme={tenantTheme(ctx.tenant.accent_color, mode)} mode={mode}>
      <TenantContext.Provider value={ctx}>
        <Suspense fallback={<LoadingView fullScreen />}>
        <Routes>
          <Route path="owner/*" element={<OwnerRoot />} />
          <Route element={<ClientShell />}>
            <Route index element={<HomePage />} />
            <Route path="services" element={<ServicesPage />} />
            <Route path="my" element={<MyBookingsPage />} />
            <Route path="assistant" element={<AssistantPage />} />
            <Route path="book" element={<Navigate to="../?book=service" replace />} />
            <Route path="*" element={<NotFoundView title="Страница не найдена" />} />
          </Route>
        </Routes>
        </Suspense>
      </TenantContext.Provider>
    </Theme>
  );
}
